/**
 * Admin Control Panel Component
 * Mobile interface for family admins to control the live quiz
 */

import React, { useCallback, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Dimensions,
  RefreshControl,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import LottieView from "lottie-react-native";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { useQuizLiveAdmin } from "../hooks/useQuizLiveAdmin";
import { LiveQuizParticipant, RankingEntry, QuizNotificationConfig, TeamMemberOption } from "../types";
import { PausedModal } from "./PausedModal";
import { LiveLogsDrawer } from "@/modules/admin-debug/LiveLogsDrawer";
import { useAuth } from "@/providers/AuthProvider";
import { ReassignCaptainModal } from "./ReassignCaptainModal";
import { QuizNotificationModal } from "./QuizNotificationModal";

const { width: SCREEN_WIDTH } = Dimensions.get("window");

interface AdminControlPanelProps {
  sessionId?: string;
  quizId?: string;
  onClose: () => void;
}

/**
 * Admin Control Panel - Controls for family admin
 */
export const AdminControlPanel: React.FC<AdminControlPanelProps> = ({
  sessionId,
  quizId,
  onClose,
}) => {
  const colors = useThemeColors();
  const insets = useSafeAreaInsets();
  const { roles } = useAuth();
  const [isCreating, setIsCreating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [teamMembersForReassign, setTeamMembersForReassign] = useState<TeamMemberOption[]>([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [showLogsDrawer, setShowLogsDrawer] = useState(false);
  
  const {
    session,
    quiz,
    participants,
    rankings,
    currentQuestion,
    currentSection,
    questionAnswers,
    allReady,
    questionPhase,
    countdownSeconds,
    lastQuestionResult,
    participantResults,
    isPaused,
    pausedForParticipant,
    pauseTimeRemaining,
    disconnectedCaptains,
    participantConnections,
    isLoading,
    isConnected,
    error,
    createSession,
    loadSession,
    startQuiz,
    nextQuestion,
    showAnswer,
    showResults,
    showRanking,
    startNextSection,
    endQuiz,
    cancelQuiz,
    pauseQuiz,
    resumeQuiz,
    getTeamMembers,
    reassignCaptain,
    sendQuizNotification,
    refreshData,
  } = useQuizLiveAdmin({ sessionId });
  
  /**
   * Handle pull-to-refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await refreshData();
    setIsRefreshing(false);
  }, [refreshData]);
  
  /**
   * Handle open notification modal
   */
  const handleOpenNotificationModal = useCallback(() => {
    setShowNotificationModal(true);
  }, []);
  
  /**
   * Handle create session with notification
   */
  const handleCreateSession = useCallback(async (config?: QuizNotificationConfig) => {
    if (!quizId) {
      console.error("No quizId provided");
      Alert.alert("Fout", "Geen quiz geselecteerd");
      return;
    }
    
    setShowNotificationModal(false);
    setIsCreating(true);
    
    try {
      const newSessionId = await createSession(quizId, config);
      
      if (newSessionId) {
        // Session created successfully - send notification with sessionId
        if (config) {
          await sendNotificationForSession(newSessionId, config);
        }
      } else {
        // Session creation failed - error should be set in hook
        Alert.alert("Fout", error || "Kon sessie niet aanmaken");
      }
    } catch (err) {
      console.error("Error creating session:", err);
      Alert.alert("Fout", "Er is iets misgegaan bij het aanmaken van de sessie");
    } finally {
      setIsCreating(false);
    }
  }, [quizId, createSession, error]);
  
  /**
   * Send notification with specific sessionId
   */
  const sendNotificationForSession = useCallback(async (sessionId: string, config: QuizNotificationConfig) => {
    try {
      // Get all team members
      const { data: allMembers } = await supabase
        .from("team_members")
        .select("user_id");
      
      if (!allMembers || allMembers.length === 0) return;
      
      const userIds = [...new Set(allMembers.map(m => m.user_id))];
      
      // Create in-app notifications using the correct "notifications" table
      const notifications = userIds.map(userId => ({
        user_id: userId,
        type: "game_challenge", // Required column
        title: config.title,
        body: `Doe snel mee met de Live Quiz!`,
        notification_type: "game_challenge",
        lottie_animation: config.lottie_animation,
        is_read: false,
        action_url: `/games/quiz/live?sessionId=${sessionId}`,
        action_data: { 
          route: `/games/quiz/live?sessionId=${sessionId}`,
          sessionId: sessionId,
        },
      }));
      
      const { error: notifError } = await supabase
        .from("notifications")
        .insert(notifications);
      
      if (notifError) {
        console.error("Error creating notifications:", notifError);
      }
      
      // Note: Push notifications are sent automatically via database trigger (on_notification_push)
      // The trigger calls the send-push-notification edge function for game_challenge notifications
      console.log(`[Quiz] Created ${notifications.length} notifications for live quiz ${sessionId}`);
    } catch (err) {
      console.error("Error sending notification:", err);
    }
  }, []);
  
  /**
   * Handle reassign captain
   */
  const handleOpenReassignModal = useCallback(async () => {
    if (!pausedForParticipant?.team_id) return;
    
    setLoadingTeamMembers(true);
    setShowReassignModal(true);
    
    const members = await getTeamMembers(pausedForParticipant.team_id);
    setTeamMembersForReassign(members);
    setLoadingTeamMembers(false);
  }, [pausedForParticipant?.team_id, getTeamMembers]);
  
  /**
   * Handle select new captain
   */
  const handleSelectNewCaptain = useCallback(async (userId: string) => {
    if (!pausedForParticipant?.id) return;
    
    setShowReassignModal(false);
    await reassignCaptain(pausedForParticipant.id, userId);
  }, [pausedForParticipant?.id, reassignCaptain]);
  
  /**
   * Handle start quiz
   */
  const handleStartQuiz = useCallback(async () => {
    if (!allReady) {
      Alert.alert(
        "Nog niet klaar",
        `${participants.filter(p => !p.is_ready).length} team(s) zijn nog niet klaar.`,
        [
          { text: "Wachten", style: "cancel" },
          { text: "Toch starten", onPress: startQuiz },
        ]
      );
      return;
    }
    
    await startQuiz();
  }, [allReady, participants, startQuiz]);
  
  /**
   * Handle next question
   */
  const handleNextQuestion = useCallback(async () => {
    await showAnswer(); // First show the answer
    
    // Then after a short delay, move to next
    Alert.alert(
      "Volgende vraag?",
      "Wil je naar de volgende vraag of eerst de tussenstand tonen?",
      [
        { text: "Tussenstand", onPress: showRanking },
        { text: "Volgende vraag", onPress: nextQuestion },
      ]
    );
  }, [showAnswer, showRanking, nextQuestion]);
  
  /**
   * Handle end quiz
   */
  const handleEndQuiz = useCallback(async () => {
    Alert.alert(
      "Quiz beëindigen?",
      "Weet je zeker dat je de quiz wilt beëindigen? De eindstand wordt getoond.",
      [
        { text: "Annuleren", style: "cancel" },
        { text: "Beëindigen", onPress: endQuiz },
      ]
    );
  }, [endQuiz]);
  
  /**
   * Handle cancel quiz
   */
  const handleCancelQuiz = useCallback(async () => {
    Alert.alert(
      "Quiz annuleren?",
      "Weet je zeker dat je de quiz wilt annuleren? Alle voortgang gaat verloren.",
      [
        { text: "Terug", style: "cancel" },
        {
          text: "Annuleren",
          style: "destructive",
          onPress: async () => {
            await cancelQuiz();
            onClose();
          },
        },
      ]
    );
  }, [cancelQuiz, onClose]);
  
  // If no session, show create option
  if (!session && quizId) {
    return (
      <View className="flex-1" style={{ backgroundColor: colors.background }}>
        <View className="px-4 py-4 flex-row items-center border-b" style={{ borderBottomColor: colors.border }}>
          <TouchableOpacity onPress={onClose}>
            <Ionicons name="close" size={28} color={colors.text} />
          </TouchableOpacity>
          <Text className="text-xl font-bold ml-4" style={{ color: colors.text }}>
            Quiz Beheren
          </Text>
        </View>
        
        <View className="flex-1 items-center justify-center p-8">
          <View className="w-20 h-20 rounded-2xl items-center justify-center mb-6" style={{ backgroundColor: colors.primaryLight }}>
            <Ionicons name="play-circle" size={48} color={colors.primary} />
          </View>
          
          <Text className="text-xl font-bold text-center" style={{ color: colors.text }}>
            Start Live Quiz
          </Text>
          <Text className="text-center mt-2 mb-8" style={{ color: colors.textSecondary }}>
            Maak een nieuwe live quiz sessie aan. Teams kunnen dan meedoen via de app.
          </Text>
          
          <TouchableOpacity
            onPress={handleOpenNotificationModal}
            disabled={isCreating}
            className="px-8 py-4 rounded-2xl flex-row items-center"
            style={{ backgroundColor: colors.primary }}
          >
            {isCreating ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <>
                <Ionicons name="rocket" size={24} color="#FFFFFF" />
                <Text className="text-white text-lg font-bold ml-2">
                  Sessie Aanmaken
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
        
        {/* Notification Customization Modal */}
        <QuizNotificationModal
          visible={showNotificationModal}
          quizName={quiz?.name || "Live Quiz"}
          onConfirm={handleCreateSession}
          onClose={() => setShowNotificationModal(false)}
        />
      </View>
    );
  }
  
  // Loading state
  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.background }}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }
  
  const readyCount = participants.filter(p => p.is_ready).length;
  const answeredCount = questionAnswers.length;
  const quizColor = quiz?.color || "#8B5CF6";
  
  return (
    <View className="flex-1" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <LinearGradient
        colors={[quizColor, "#4F46E5"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        className="px-4 pb-4"
        style={{ paddingTop: insets.top + 16 }}
      >
        <View className="flex-row items-center justify-between">
          <TouchableOpacity onPress={onClose}>
            <Ionicons name="close" size={28} color="#FFFFFF" />
          </TouchableOpacity>
          
          <View className="flex-row items-center gap-3">
            <TouchableOpacity
              onLongPress={() => roles.isSuperAdmin && setShowLogsDrawer(prev => !prev)}
              delayLongPress={800}
              className="flex-row items-center px-3 py-1 rounded-full bg-white/20"
              accessibilityLabel="Verbindingsstatus"
            >
              <View className={`w-2 h-2 rounded-full mr-2 ${isConnected ? "bg-green-400" : "bg-red-400"}`} />
              <Text className="text-white text-sm">
                {isConnected ? "Verbonden" : "..."}
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              onPress={handleCancelQuiz}
              className="px-3 py-1 rounded-full bg-red-500/30"
            >
              <Text className="text-white text-sm">Annuleren</Text>
            </TouchableOpacity>
          </View>
        </View>
        
        <View className="mt-4">
          <Text className="text-white/80">Quiz Master</Text>
          <Text className="text-white text-xl font-bold">{quiz?.name || "Live Quiz"}</Text>
        </View>
        
        {/* Status Badge */}
        <View className="mt-3 self-start px-3 py-1 rounded-full bg-white/20">
          <Text className="text-white text-sm font-medium capitalize">
            {session?.status === "lobby" && "🏠 Lobby"}
            {session?.status === "playing" && `❓ Vraag ${(session.current_question_index || 0) + 1}/${session.total_questions}`}
            {session?.status === "paused" && "⏸️ Gepauzeerd"}
            {session?.status === "showing_ranking" && "🏆 Tussenstand"}
            {session?.status === "section_break" && "☕ Pauze"}
            {session?.status === "completed" && "✅ Afgerond"}
          </Text>
        </View>
      </LinearGradient>
      
      {/* Error */}
      {error && (
        <View className="mx-4 mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30">
          <Text className="text-red-500">{error}</Text>
        </View>
      )}
      
      {/* Content */}
      <ScrollView 
        className="flex-1 px-4 py-4"
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={quiz?.color || colors.primary}
            colors={[quiz?.color || colors.primary]}
          />
        }
      >
        {/* Lobby State */}
        {session?.status === "lobby" && (
          <>
            {/* Teams List */}
            <View className="mb-4">
              <View className="flex-row items-center justify-between mb-3">
                <Text className="text-lg font-semibold" style={{ color: colors.text }}>
                  Teams ({readyCount}/{participants.length} klaar)
                </Text>
                {allReady && participants.length > 0 && (
                  <View className="px-2 py-1 rounded-full bg-green-500/20">
                    <Text className="text-green-600 text-xs font-medium">Alle teams klaar!</Text>
                  </View>
                )}
              </View>
              
              {participants.length === 0 ? (
                <View
                  className="p-6 rounded-xl items-center"
                  style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
                >
                  <Ionicons name="hourglass-outline" size={48} color={colors.textTertiary} />
                  <Text className="mt-3" style={{ color: colors.textSecondary }}>
                    Wachten op teams...
                  </Text>
                  <Text className="text-sm mt-2 text-center" style={{ color: colors.textTertiary }}>
                    Teams kunnen meedoen via de app door naar deze quiz te navigeren.
                  </Text>
                </View>
              ) : (
                <View className="gap-2">
                  {participants.map((p) => (
                    <ParticipantRow
                      key={p.id}
                      participant={p}
                      quizColor={quizColor}
                      connectionStatus={participantConnections[p.id] || "offline"}
                    />
                  ))}
                </View>
              )}
            </View>
            
            {/* Session Code */}
            <View
              className="p-4 rounded-xl mb-4"
              style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
            >
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                Sessie ID (voor web display):
              </Text>
              <Text className="font-mono text-lg font-bold mt-1" style={{ color: colors.text }}>
                {session?.id?.slice(0, 8).toUpperCase()}
              </Text>
            </View>
          </>
        )}
        
        {/* Disconnected captains warning banner */}
        {session?.status === "playing" && disconnectedCaptains.length > 0 && (
          <View
            className="p-3 rounded-xl mb-4 flex-row items-center"
            style={{ backgroundColor: "#F59E0B20", borderWidth: 1, borderColor: "#F59E0B" }}
          >
            <Ionicons name="warning" size={20} color="#F59E0B" />
            <View className="flex-1 ml-2">
              <Text className="font-semibold" style={{ color: "#F59E0B" }}>
                Verbinding verloren
              </Text>
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                {disconnectedCaptains.map(p => p.team?.name || "Team").join(", ")} — pauzeren als dit aanhoudt
              </Text>
            </View>
          </View>
        )}

        {/* Playing State - Countdown Phase */}
        {session?.status === "playing" && questionPhase === "countdown" && (
          <View
            className="p-8 rounded-2xl items-center mb-4"
            style={{ backgroundColor: `${quizColor}20`, borderWidth: 2, borderColor: quizColor }}
          >
            <Text className="text-lg mb-2" style={{ color: colors.textSecondary }}>
              Vraag {(session.current_question_index || 0) + 1} van {session.total_questions}
            </Text>
            <Text className="text-2xl font-bold mb-4" style={{ color: colors.text }}>
              🎯 Klaar voor de volgende vraag...
            </Text>
            <View
              className="w-24 h-24 rounded-full items-center justify-center"
              style={{ backgroundColor: quizColor }}
            >
              <Text className="text-5xl font-black text-white">
                {countdownSeconds > 0 ? countdownSeconds : "GO!"}
              </Text>
            </View>
          </View>
        )}
        
        {/* Playing State - Active Question */}
        {session?.status === "playing" && questionPhase === "active" && currentQuestion && (
          <>
            {/* Current Question */}
            <View
              className="p-4 rounded-xl mb-4"
              style={{ backgroundColor: `${quizColor}15`, borderWidth: 2, borderColor: quizColor }}
            >
              <View className="flex-row items-center justify-between mb-2">
                <Text className="font-semibold" style={{ color: quizColor }}>
                  Vraag {(session.current_question_index || 0) + 1} van {session.total_questions}
                </Text>
                <View className="flex-row items-center">
                  <Ionicons name="people" size={16} color={colors.textSecondary} />
                  <Text className="ml-1" style={{ color: colors.textSecondary }}>
                    {answeredCount}/{participants.length} beantwoord
                  </Text>
                </View>
              </View>
              
              <Text className="text-lg font-semibold" style={{ color: colors.text }}>
                {currentQuestion.question}
              </Text>
              
              {currentQuestion.image_url && (
                <Text className="text-sm mt-2" style={{ color: colors.textSecondary }}>
                  📷 Met afbeelding
                </Text>
              )}
              
              <View className="mt-3 gap-1">
                {currentQuestion.options.map((option, idx) => (
                  <View key={idx} className="flex-row items-center">
                    <View
                      className="w-6 h-6 rounded items-center justify-center mr-2"
                      style={{ backgroundColor: colors.surfaceSecondary }}
                    >
                      <Text style={{ color: colors.textSecondary }}>{String.fromCharCode(65 + idx)}</Text>
                    </View>
                    <Text style={{ color: colors.text }}>{option.text}</Text>
                    {option.is_correct && (
                      <Ionicons name="checkmark-circle" size={16} color="#10B981" className="ml-2" />
                    )}
                  </View>
                ))}
              </View>
            </View>
            
            {/* Answer Progress */}
            <View
              className="p-4 rounded-xl mb-4"
              style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
            >
              <Text className="font-semibold mb-2" style={{ color: colors.text }}>
                Antwoorden ontvangen
              </Text>
              <View className="flex-row flex-wrap gap-2">
                {participants.map((p) => {
                  const hasAnswered = questionAnswers.some(a => a.participant_id === p.id);
                  const conn = participantConnections[p.id] || "offline";
                  const dotColor = conn === "online" ? "#10B981" : conn === "warning" ? "#F59E0B" : "#EF4444";
                  return (
                    <View
                      key={p.id}
                      className="px-3 py-1 rounded-full flex-row items-center"
                      style={{ backgroundColor: hasAnswered ? "#10B98120" : colors.surfaceSecondary }}
                    >
                      <View className="w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: dotColor }} />
                      <Text style={{ color: hasAnswered ? "#10B981" : colors.textSecondary }}>
                        {p.team?.name}
                        {hasAnswered && " ✓"}
                      </Text>
                    </View>
                  );
                })}
              </View>
            </View>
          </>
        )}
        
        {/* Playing State - Results Phase */}
        {session?.status === "playing" && questionPhase === "results" && currentQuestion && lastQuestionResult && (
          <>
            {/* Correct Answer */}
            <View
              className="p-4 rounded-xl mb-4 items-center"
              style={{ backgroundColor: "#10B98120", borderWidth: 2, borderColor: "#10B981" }}
            >
              <Text className="font-semibold mb-2" style={{ color: "#10B981" }}>
                ✓ Het juiste antwoord is:
              </Text>
              <Text className="text-xl font-bold text-center" style={{ color: colors.text }}>
                {currentQuestion.options[lastQuestionResult.correct_option_index]?.text}
              </Text>
            </View>
            
            {/* Fastest Correct */}
            {lastQuestionResult.fastest_correct && (
              <View
                className="p-4 rounded-xl mb-4"
                style={{ backgroundColor: "#F59E0B20", borderWidth: 2, borderColor: "#F59E0B" }}
              >
                <Text className="font-semibold mb-2" style={{ color: "#F59E0B" }}>
                  ⚡ Snelste juiste antwoord:
                </Text>
                <Text className="text-lg font-bold" style={{ color: colors.text }}>
                  {lastQuestionResult.fastest_correct.team_name}
                </Text>
                <Text style={{ color: colors.textSecondary }}>
                  in {(lastQuestionResult.fastest_correct.time_ms / 1000).toFixed(2)}s
                </Text>
              </View>
            )}
            
            {/* All Results */}
            <View
              className="p-4 rounded-xl mb-4"
              style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
            >
              <Text className="font-semibold mb-3" style={{ color: colors.text }}>
                Resultaten
              </Text>
              <View className="gap-2">
                {participantResults.map((pResult) => (
                  <View
                    key={pResult.participantId}
                    className="flex-row items-center p-2 rounded-lg"
                    style={{ 
                      backgroundColor: pResult.isCorrect ? "#10B98115" : "#EF444415",
                    }}
                  >
                    <View
                      className="w-8 h-8 rounded-full items-center justify-center mr-3"
                      style={{ backgroundColor: pResult.isCorrect ? "#10B981" : "#EF4444" }}
                    >
                      <Ionicons 
                        name={pResult.isCorrect ? "checkmark" : "close"} 
                        size={20} 
                        color="#FFFFFF" 
                      />
                    </View>
                    <View className="flex-1">
                      <Text className="font-semibold" style={{ color: colors.text }}>
                        {pResult.teamName}
                      </Text>
                      <Text className="text-xs" style={{ color: colors.textSecondary }}>
                        {pResult.selectedOption !== null 
                          ? `${(pResult.timeTakenMs / 1000).toFixed(2)}s`
                          : "Geen antwoord"
                        }
                      </Text>
                    </View>
                    <Text 
                      className="text-lg font-bold"
                      style={{ color: pResult.isCorrect ? "#10B981" : "#EF4444" }}
                    >
                      {pResult.isCorrect 
                        ? `+${pResult.pointsEarned + pResult.bonusEarned}` 
                        : "0"
                      }
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          </>
        )}
        
        {/* Rankings */}
        {(session?.status === "showing_ranking" || session?.status === "completed") && (
          <RankingsList rankings={rankings} quizColor={quizColor} />
        )}
      </ScrollView>
      
      {/* Control Buttons */}
      <View className="px-4 pt-4" style={{ backgroundColor: colors.background, paddingBottom: Math.max(insets.bottom, 16) + 8 }}>
        {session?.status === "lobby" && (
          <TouchableOpacity
            onPress={handleStartQuiz}
            disabled={participants.length === 0}
            className="rounded-2xl p-4 flex-row items-center justify-center"
            style={{
              backgroundColor: participants.length > 0 ? "#10B981" : colors.surfaceSecondary,
            }}
          >
            <Ionicons name="play" size={24} color="#FFFFFF" />
            <Text className="text-white text-lg font-bold ml-2">
              Start Quiz
            </Text>
          </TouchableOpacity>
        )}
        
        {/* Playing - Countdown phase (no buttons, auto-transitions) */}
        {session?.status === "playing" && questionPhase === "countdown" && (
          <View className="items-center py-4">
            <ActivityIndicator color={quizColor} />
            <Text className="mt-2" style={{ color: colors.textSecondary }}>
              Countdown loopt...
            </Text>
          </View>
        )}
        
        {/* Playing - Active question phase */}
        {session?.status === "playing" && questionPhase === "active" && (
          <View className="flex-row gap-3">
            <TouchableOpacity
              onPress={showResults}
              className="flex-1 rounded-2xl p-4 items-center"
              style={{ backgroundColor: "#F59E0B" }}
            >
              <Ionicons name="eye" size={24} color="#FFFFFF" />
              <Text className="text-white font-bold mt-1">Toon Resultaat</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              onPress={() => {
                Alert.alert(
                  "Sla vraag over?",
                  "Weet je zeker dat je deze vraag wilt overslaan?",
                  [
                    { text: "Annuleren", style: "cancel" },
                    { text: "Overslaan", onPress: nextQuestion },
                  ]
                );
              }}
              className="rounded-2xl p-4 items-center"
              style={{ backgroundColor: colors.surfaceSecondary }}
            >
              <Ionicons name="play-skip-forward" size={24} color={colors.textSecondary} />
              <Text className="font-bold mt-1" style={{ color: colors.textSecondary }}>Skip</Text>
            </TouchableOpacity>
          </View>
        )}
        
        {/* Playing - Results phase */}
        {session?.status === "playing" && questionPhase === "results" && (
          <View className="flex-row gap-3">
            <TouchableOpacity
              onPress={showRanking}
              className="flex-1 rounded-2xl p-4 items-center"
              style={{ backgroundColor: colors.surfaceSecondary }}
            >
              <Ionicons name="trophy" size={24} color={colors.textSecondary} />
              <Text className="font-bold mt-1" style={{ color: colors.textSecondary }}>Tussenstand</Text>
            </TouchableOpacity>
            
            {/* Show "Finish Quiz" on last question, otherwise "Next Question" */}
            {(session.current_question_index || 0) >= (session.total_questions || 1) - 1 ? (
              <TouchableOpacity
                onPress={handleEndQuiz}
                className="flex-1 rounded-2xl p-4 items-center"
                style={{ backgroundColor: "#10B981" }}
              >
                <Ionicons name="checkmark-done" size={24} color="#FFFFFF" />
                <Text className="text-white font-bold mt-1">Quiz Afronden</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                onPress={nextQuestion}
                className="flex-1 rounded-2xl p-4 items-center"
                style={{ backgroundColor: quizColor }}
              >
                <Ionicons name="arrow-forward" size={24} color="#FFFFFF" />
                <Text className="text-white font-bold mt-1">Volgende Vraag</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
        
        {session?.status === "showing_ranking" && (
          <View className="flex-row gap-3">
            <TouchableOpacity
              onPress={nextQuestion}
              className="flex-1 rounded-2xl p-4 items-center"
              style={{ backgroundColor: quizColor }}
            >
              <Ionicons name="arrow-forward" size={24} color="#FFFFFF" />
              <Text className="text-white font-bold mt-1">Volgende Vraag</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              onPress={handleEndQuiz}
              className="rounded-2xl p-4 items-center"
              style={{ backgroundColor: colors.surfaceSecondary }}
            >
              <Ionicons name="stop" size={24} color={colors.textSecondary} />
              <Text className="font-bold mt-1" style={{ color: colors.textSecondary }}>Stop</Text>
            </TouchableOpacity>
          </View>
        )}
        
        {session?.status === "completed" && (
          <TouchableOpacity
            onPress={onClose}
            className="rounded-2xl p-4 flex-row items-center justify-center"
            style={{ backgroundColor: quizColor }}
          >
            <Ionicons name="checkmark-circle" size={24} color="#FFFFFF" />
            <Text className="text-white text-lg font-bold ml-2">
              Quiz Afsluiten
            </Text>
          </TouchableOpacity>
        )}
        
        {/* Resume button when paused */}
        {session?.status === "paused" && (
          <View className="gap-3">
            <TouchableOpacity
              onPress={resumeQuiz}
              className="rounded-2xl p-4 flex-row items-center justify-center"
              style={{ backgroundColor: "#10B981" }}
            >
              <Ionicons name="play" size={24} color="#FFFFFF" />
              <Text className="text-white text-lg font-bold ml-2">
                Quiz Hervatten
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              onPress={handleOpenReassignModal}
              className="rounded-2xl p-4 flex-row items-center justify-center"
              style={{ backgroundColor: "#F59E0B" }}
            >
              <Ionicons name="people" size={24} color="#FFFFFF" />
              <Text className="text-white font-bold ml-2">
                Nieuw Teamlid Aanwijzen
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
      
      {/* Paused Modal */}
      <PausedModal
        visible={isPaused}
        playerName={pausedForParticipant?.player?.full_name || pausedForParticipant?.player?.nickname || "Onbekend"}
        timeRemaining={pauseTimeRemaining}
        isAdmin={true}
        isPlayerBack={
          pausedForParticipant
            ? (participantConnections[pausedForParticipant.id] === "online")
            : false
        }
        onResume={resumeQuiz}
        onReassignCaptain={handleOpenReassignModal}
        onCancelQuiz={handleCancelQuiz}
      />
      
      {/* Reassign Captain Modal */}
      <ReassignCaptainModal
        visible={showReassignModal}
        participant={pausedForParticipant}
        isLoading={loadingTeamMembers}
        teamMembers={teamMembersForReassign}
        onSelectMember={handleSelectNewCaptain}
        onClose={() => setShowReassignModal(false)}
      />

      {/* Live Logs Drawer (super-admin only, toggled via long-press on Verbonden badge) */}
      {roles.isSuperAdmin && session?.id && (
        <LiveLogsDrawer
          sessionId={session.id}
          visible={showLogsDrawer}
          onClose={() => setShowLogsDrawer(false)}
        />
      )}
    </View>
  );
};

/**
 * Participant Row
 */
interface ParticipantRowProps {
  participant: LiveQuizParticipant;
  quizColor: string;
  connectionStatus?: "online" | "warning" | "offline";
}

const ParticipantRow: React.FC<ParticipantRowProps> = ({ participant, quizColor, connectionStatus = "offline" }) => {
  const colors = useThemeColors();
  const dotColor = connectionStatus === "online" ? "#10B981" : connectionStatus === "warning" ? "#F59E0B" : "#EF4444";
  
  return (
    <View
      className="rounded-xl p-3 flex-row items-center"
      style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
    >
      {/* Connection dot */}
      <View className="w-2.5 h-2.5 rounded-full mr-2" style={{ backgroundColor: dotColor }} />

      {/* Mascot */}
      <View
        className="w-10 h-10 rounded-lg overflow-hidden items-center justify-center"
        style={{ backgroundColor: colors.surfaceSecondary }}
      >
        <Text className="text-2xl">🐾</Text>
      </View>
      
      <View className="flex-1 ml-3">
        <Text className="font-semibold" style={{ color: colors.text }}>
          {participant.team?.name || "Team"}
        </Text>
        <Text className="text-sm" style={{ color: colors.textSecondary }}>
          {participant.player?.full_name || "Speler"}
        </Text>
      </View>
      
      <View
        className="px-3 py-1 rounded-full"
        style={{ backgroundColor: participant.is_ready ? "#10B98120" : colors.surfaceSecondary }}
      >
        <Text style={{ color: participant.is_ready ? "#10B981" : colors.textSecondary }}>
          {participant.is_ready ? "✓ Klaar" : "Wacht..."}
        </Text>
      </View>
    </View>
  );
};

/**
 * Rankings List
 */
interface RankingsListProps {
  rankings: RankingEntry[];
  quizColor: string;
}

const RankingsList: React.FC<RankingsListProps> = ({ rankings, quizColor }) => {
  const colors = useThemeColors();
  
  const getMedalEmoji = (rank: number) => {
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return `#${rank}`;
  };
  
  return (
    <View className="gap-2">
      <Text className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
        🏆 Tussenstand
      </Text>
      
      {rankings.map((entry) => (
        <View
          key={entry.participant.id}
          className="rounded-xl p-4 flex-row items-center"
          style={{
            backgroundColor: entry.rank <= 3 ? `${quizColor}15` : colors.surface,
            borderWidth: entry.rank <= 3 ? 2 : 1,
            borderColor: entry.rank <= 3 ? quizColor : colors.border,
          }}
        >
          <Text className="text-2xl mr-3">{getMedalEmoji(entry.rank)}</Text>
          
          {entry.mascotLottie?.source && (
            <View className="w-12 h-12 rounded-lg overflow-hidden mr-3" style={{ backgroundColor: colors.surfaceSecondary }}>
              <LottieView
                source={entry.mascotLottie.source}
                autoPlay
                loop
                style={{ width: 48, height: 48 }}
              />
            </View>
          )}
          
          <View className="flex-1">
            <Text className="font-bold" style={{ color: colors.text }}>
              {entry.participant.team?.name || "Team"}
            </Text>
            <Text className="text-sm" style={{ color: colors.textSecondary }}>
              {entry.correct_answers} correct
            </Text>
          </View>
          
          <View className="items-end">
            <Text className="text-xl font-bold" style={{ color: quizColor }}>
              {entry.total_score}
            </Text>
            <Text className="text-xs" style={{ color: colors.textSecondary }}>
              punten
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
};

